import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";
import { Spinner } from "./Spinner";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "font-semibold rounded-lg",
    "transition-all duration-200",
    "focus:outline-none focus:ring-2 focus:ring-offset-2",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "select-none whitespace-nowrap",
    "active:scale-[0.98]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-blue-700 text-white hover:bg-blue-800 active:bg-blue-900 focus:ring-blue-500 shadow-sm hover:shadow-md",
        secondary:
          "bg-gray-100 text-gray-800 hover:bg-gray-200 active:bg-gray-300 focus:ring-gray-400",
        outline:
          "border-2 border-blue-700 text-blue-700 hover:bg-blue-50 active:bg-blue-100 focus:ring-blue-500 bg-transparent",
        ghost:
          "text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-300 bg-transparent",
        danger:
          "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus:ring-red-500 shadow-sm hover:shadow-md",
        success:
          "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 focus:ring-green-500 shadow-sm hover:shadow-md",
        "outline-danger":
          "border-2 border-red-600 text-red-600 hover:bg-red-50 active:bg-red-100 focus:ring-red-500 bg-transparent",
      },
      size: {
        xs: "px-2.5 py-1 text-xs rounded-md",
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2.5 text-sm",
        lg: "px-6 py-3 text-base",
        xl: "px-8 py-4 text-lg",
        icon: "p-2 rounded-lg",
        "icon-sm": "p-1.5 rounded-md",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
      rounded: {
        default: "",
        full: "rounded-full",
        none: "rounded-none",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
      rounded: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      rounded,
      loading = false,
      loadingText,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          buttonVariants({ variant, size, fullWidth, rounded }),
          className,
        )}
        {...props}
      >
        {loading ? (
          <Spinner size="sm" color="current" />
        ) : leftIcon ? (
          <span className="shrink-0" aria-hidden="true">
            {leftIcon}
          </span>
        ) : null}

        <span>{loading && loadingText ? loadingText : children}</span>

        {!loading && rightIcon && (
          <span className="shrink-0" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

// Day 9 complete
